import { memo, useState, useEffect } from 'react';
import { Box, Typography, Stack, Alert, Paper } from '@mui/material';
import { ScreenRotation as RotateIcon } from '@mui/icons-material';
import { useThemeContext } from '../../contexts/ThemeContext';

const HeatmapChart = ({ data }) => {
  const [screenSize, setScreenSize] = useState({ width: 1440, height: 900 });
  const { theme } = useThemeContext();
  const heatmapTheme = theme.heatmapColors;

  useEffect(() => {
    const updateScreenSize = () => {
      setScreenSize({ width: window.innerWidth, height: window.innerHeight });
    };

    // Set initial size
    if (typeof window !== 'undefined') {
      updateScreenSize();
      window.addEventListener('resize', updateScreenSize);
      return () => window.removeEventListener('resize', updateScreenSize);
    }
  }, []);
  if (!data || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography variant="body1" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  // Get unique species and hours
  const species = [...new Set(data.map(d => d.species))].sort();
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  // Create data matrix
  const matrix = {};
  species.forEach(s => {
    matrix[s] = {};
    hours.forEach(h => {
      matrix[s][h] = 0;
    });
  });
  
  // Fill matrix with data
  data.forEach(d => {
    if (matrix[d.species]) {
      matrix[d.species][d.hour] = d.count;
    }
  });
  
  // Find max count for color scaling
  const maxCount = Math.max(...data.map(d => d.count));
  
  const getColor = (count) => {
    if (count === 0) return heatmapTheme.legendColors.none;
    const intensity = count / maxCount;
    return heatmapTheme.heatmapGradient.replace('{intensity}', Math.max(0.2, intensity));
  };

  // Responsive cell sizing based on screen width
  const getResponsiveSize = () => {
    const screenWidth = screenSize.width;
    if (screenWidth >= 1920) return { cellSize: 45, labelWidth: 250 }; // Large monitors
    if (screenWidth >= 1440) return { cellSize: 35, labelWidth: 200 }; // Desktop
    if (screenWidth >= 1024) return { cellSize: 30, labelWidth: 160 }; // Small desktop/tablet
    if (screenWidth >= 768) return { cellSize: 25, labelWidth: 140 };   // Tablet
    return { cellSize: 20, labelWidth: 120 }; // Mobile
  };

  const { cellSize, labelWidth } = getResponsiveSize();
  const headerHeight = Math.max(30, cellSize * 0.8);

  // Check if mobile in portrait mode
  const isMobilePortrait = screenSize.width < 768 && screenSize.height > screenSize.width;

  // Convert 24-hour format to 12-hour format with am/pm
  const formatHour = (hour) => {
    if (hour === 0) return '12am';
    if (hour === 12) return '12pm';
    if (hour < 12) return `${hour}am`;
    return `${hour - 12}pm`;
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Mobile Portrait Message */}
      {isMobilePortrait && (
        <Alert 
          severity="info" 
          icon={<RotateIcon />}
          sx={{ 
            mb: 2, 
            width: '100%',
            maxWidth: 400,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight="medium">
              Better viewing experience
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rotate your device to landscape for optimal heatmap viewing
            </Typography>
          </Box>
        </Alert>
      )}
      
      <Box sx={{ overflow: 'auto', maxWidth: '100%' }}>
        <svg 
          width={labelWidth + hours.length * cellSize} 
          height={headerHeight + species.length * cellSize}
          style={{ 
            border: `2px solid ${heatmapTheme.border}`, 
            borderRadius: '8px',
            backgroundColor: heatmapTheme.background
          }}
        >
        {/* Hour labels */}
        {hours.map((hour, i) => (
          <text
            key={hour}
            x={labelWidth + i * cellSize + cellSize / 2}
            y={headerHeight / 2 + 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={heatmapTheme.textSecondary}
            style={{ fontSize: Math.max(10, cellSize * 0.35) + 'px' }}
          >
            {formatHour(hour)}
          </text>
        ))}
        
        {/* Species labels and cells */}
        {species.map((speciesName, i) => (
          <g key={speciesName}>
            {/* Species label */}
            <text
              x={labelWidth - 8}
              y={headerHeight + i * cellSize + cellSize / 2}
              textAnchor="end"
              dominantBaseline="middle"
              fill={heatmapTheme.textPrimary}
              style={{ fontSize: Math.max(10, cellSize * 0.35) + 'px' }}
            >
              {speciesName.length > Math.floor(labelWidth / 8) ? 
                speciesName.substring(0, Math.floor(labelWidth / 10)) + '...' : 
                speciesName
              }
            </text>
            
            {/* Hour cells for this species */}
            {hours.map((hour, j) => {
              const count = matrix[speciesName][hour];
              return (
                <g key={`${speciesName}-${hour}`}>
                  <rect
                    x={labelWidth + j * cellSize}
                    y={headerHeight + i * cellSize}
                    width={cellSize}
                    height={cellSize}
                    fill={getColor(count)}
                    stroke={heatmapTheme.border}
                    strokeWidth="1"
                  />
                  <title>{`${speciesName} at ${formatHour(hour)} - ${count} detections`}</title>
                </g>
              );
            })}
          </g>
        ))}
        </svg>
      </Box>
      
      {/* Legend */}
      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          Activity Level:
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          {Object.entries(heatmapTheme.legendColors).map(([level, color]) => (
            <Box key={level} display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: color,
                  border: `2px solid ${heatmapTheme.border}`,
                  borderRadius: 1
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default memo(HeatmapChart);